// Reusable structured address input.
// Value shape: { line1, line2, city, province, postCode, country }
// All six fields are optional. The country field is a searchable dropdown.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../i18n';
import { COUNTRIES } from '../lib/countries';
import { emptyAddress, parseAddress } from '../lib/formatters';

// Re-exported so callers can `import { AddressInput, emptyAddress } from '.../AddressInput'`
// without having to know about the formatters module.
export { emptyAddress };

export function AddressInput({ value, onChange, disabled }) {
  const { t } = useTranslation();
  const address = parseAddress(value);

  function update(field, fieldValue) {
    onChange?.({ ...address, [field]: fieldValue });
  }

  return (
    <div className="address-input">
      <label className="address-input__field">
        <span className="address-input__label">{t('address.line1')}</span>
        <input
          type="text"
          className="address-input__control"
          value={address.line1}
          placeholder={t('address.line1Placeholder')}
          autoComplete="address-line1"
          disabled={disabled}
          onChange={(e) => update('line1', e.target.value)}
        />
      </label>

      <label className="address-input__field">
        <span className="address-input__label">{t('address.line2')}</span>
        <input
          type="text"
          className="address-input__control"
          value={address.line2}
          placeholder={t('address.line2Placeholder')}
          autoComplete="address-line2"
          disabled={disabled}
          onChange={(e) => update('line2', e.target.value)}
        />
      </label>

      <div className="address-input__row">
        <label className="address-input__field">
          <span className="address-input__label">{t('address.city')}</span>
          <input
            type="text"
            className="address-input__control"
            value={address.city}
            autoComplete="address-level2"
            disabled={disabled}
            onChange={(e) => update('city', e.target.value)}
          />
        </label>

        <label className="address-input__field">
          <span className="address-input__label">{t('address.province')}</span>
          <input
            type="text"
            className="address-input__control"
            value={address.province}
            autoComplete="address-level1"
            disabled={disabled}
            onChange={(e) => update('province', e.target.value)}
          />
        </label>
      </div>

      <div className="address-input__row">
        <label className="address-input__field">
          <span className="address-input__label">{t('address.postCode')}</span>
          <input
            type="text"
            className="address-input__control"
            value={address.postCode}
            autoComplete="postal-code"
            disabled={disabled}
            onChange={(e) => update('postCode', e.target.value)}
          />
        </label>

        <CountryPicker
          value={address.country}
          onChange={(v) => update('country', v)}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Searchable country dropdown
// ─────────────────────────────────────────────────────────────────────────────

function CountryPicker({ value, onChange, disabled }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const trimmed = query.trim().toLowerCase();
  const filtered = trimmed
    ? COUNTRIES.filter((c) => c.toLowerCase().includes(trimmed))
    : COUNTRIES;

  function selectCountry(country) {
    onChange?.(country);
    setOpen(false);
    setQuery('');
  }

  return (
    <div className="address-input__field" ref={ref}>
      <span className="address-input__label">{t('address.country')}</span>
      <button
        type="button"
        className="address-input__control address-input__country-trigger"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span
          className={
            value
              ? 'address-input__country-value'
              : 'address-input__country-value address-input__country-value--placeholder'
          }
        >
          {value || t('address.countryPlaceholder')}
        </span>
        <span aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="address-input__country-menu" role="listbox">
          <input
            type="text"
            className="address-input__country-search"
            value={query}
            placeholder={t('address.countrySearch')}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <ul className="address-input__country-list">
            {value && (
              <li>
                <button
                  type="button"
                  className="address-input__country-item address-input__country-item--clear"
                  onClick={() => selectCountry('')}
                >
                  {t('address.countryClear')}
                </button>
              </li>
            )}
            {filtered.length === 0 ? (
              <li className="address-input__country-empty">{t('address.countryNoMatch')}</li>
            ) : (
              filtered.map((country) => (
                <li key={country}>
                  <button
                    type="button"
                    className={
                      'address-input__country-item' +
                      (country === value ? ' address-input__country-item--selected' : '')
                    }
                    onClick={() => selectCountry(country)}
                  >
                    {country}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
